/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {Provider} from 'react-redux';
import {ConnectorsProvider} from 'commerceAPI/contexts';

import {useLocalesMgr} from 'coreHooks';
import {useCategoryActions, useFetchAllCategories} from 'coreHooks/categories';
import {resolveSiteFromUrl} from 'coreUtils/siteUtils';
import {getConfig, isServer} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {fetchTranslations} from 'brand/app/utils/locale';
import ManagerRepository from 'brandClasses/ManagerRepository';

import {GeneralProvider} from './GeneralProvider';

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible';

const refs = new Map();

const AppIntlProvider = ({children, messages, detectedLanguage}) => {
	const {managerRepository} = useGeneralProvider();
	const categoryActions = useCategoryActions();

	managerRepository.getCategoriesMgr = () => categoryActions;
	useFetchAllCategories();

	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const language = currentLocale.getLanguageCode() || detectedLanguage;
	const localeString = localesMgr.getCurrentLocaleString();
	const [currentMessages, setCurrentMessages] = useState(messages || {});
	const messagesLocalised = currentMessages[localeString] || currentMessages[language];
	const currentAvailableLanguages = managerRepository.getLocalesMgr().getCurrentCountry()?.getAvailableLanguages();
	const languageCode = localesMgr.getCurrentLanguage()?.getLanguageCode();

	useEffect(() => {
		const languagesToFetch = [];

		if (language && !currentMessages[language]) {
			languagesToFetch.push(language);
		}

		if (localeString && !currentMessages[localeString]) {
			languagesToFetch.push(localeString);
		}

		if (languagesToFetch.length > 0) {
			fetchTranslations.apply(null, languagesToFetch).then((translations) => {
				if (translations) {
					let messages = {...currentMessages};

					messages[localeString] = translations;
					setCurrentMessages(messages);
				}
			});
		}
	}, [language, localeString]);

	useEffect(() => {
		if (currentAvailableLanguages && languageCode) {
			const isAvailableLanguage = currentAvailableLanguages.includes(languageCode);
			const language = isAvailableLanguage ? currentLocale.getLanguageCode() : currentAvailableLanguages[0];

			!isAvailableLanguage && localesMgr.updateLocale(localesMgr.getCurrentCountry(), language);
		}
	}, [currentLocale.getLanguageCode(), localesMgr.isCookiesProcessed()]);

	return (
		<IntlProvider
			onError={() => {}}
			locale={language}
			messages={messagesLocalised}
			defaultLocale={managerRepository.getLocalesMgr().getDefaultLocale().getLanguageCode()} //TODO: Fix as at this point configurations are already not set
		>
			{children}
		</IntlProvider>
	);
};

/**
 * AppConfig provides the React context tree (Redux store, connectors, intl,
 * GeneralProvider) that wraps the entire application on both server and client.
 */
const AppConfig = ({children, locals = {}, messages, detectedLanguage, envPreferences}) => {
	const [isAppLoading, setIsAppLoading] = useState(false);
	const [isLocaleCookiesProcessed, setLocaleCookiesProcessed] = useState(false);

	return (
		<Provider store={locals.managerRepository.getStoreMgr().getStore()}>
			<ConnectorsProvider value={locals.connectors}>
				<GeneralProvider value={{
					managerRepository: locals.managerRepository,
					setRef: (key, value) => refs.set(key, value),
					getRef: (key) => refs.has(key) ? refs.get(key) : {current: null},
					setIsAppLoading,
					isAppLoading,
					getEnvPreference: (key)=> envPreferences[key],
					isLocaleCookiesProcessed,
					setLocaleCookiesProcessed
				}}>
					<AppIntlProvider messages={messages} detectedLanguage={detectedLanguage}>
						{children}
					</AppIntlProvider>
				</GeneralProvider>
			</ConnectorsProvider>
		</Provider>
	);
};

AppConfig.restore = (locals = {}, frozen = {}, req, envPreferences) => {
	let initialState = null;
	const path = isServer ? locals.originalUrl : `${window.location.pathname}${window.location.search}`;
	const site = resolveSiteFromUrl(path);
	const {app: appConfig} = getConfig();
	const apiConfig = {
		...appConfig.commerceAPI,
		einsteinConfig: appConfig.einsteinAPI,
		useSLASPrivateClient: req.app.useSLASPrivateClient
	};
	apiConfig.parameters.siteId = site.id;

	if (frozen.reduxStoreState) {
		reduxStoreStateCleanUp(frozen.reduxStoreState);

		initialState = frozen.reduxStoreState;
	}

	const managerRepository = new ManagerRepository(
		initialState,
		apiConfig,
		req?.data?.staticCache,
		envPreferences
	);

	managerRepository
		.getUiMgr()
		.setRequestClasses(req.requestClasses);

	managerRepository.getSeoMgr().setDefaultMetadata({
		title: appConfig.seo.defaultTitle,
		description: appConfig.seo.defaultDescription,
		keywords: appConfig.seo.defaultKeywords,
	});

	if (!isServer) {
		// use session storage for currency to improve performance
		if (localStorage.getItem('scapi:currency')) {
			managerRepository
				.getCommerceApi()
				.setCurrency(localStorage.getItem('scapi:currency'));
		}

		if (localStorage.getItem('scapi:locale')) {
			managerRepository
				.getCommerceApi()
				.setLocale(localStorage.getItem('scapi:locale'));
		}
	}

	locals.managerRepository = managerRepository;
	locals.generalData = {};
	locals.connectors = {
		commerceAPI: managerRepository.getCommerceApi(),
		sfccAPI: managerRepository.getSfccAPI()
	};
};

AppConfig.freeze = ({managerRepository}) => {
	const store = managerRepository.getStoreMgr().getStore();

	return {
		reduxStoreState: store.getState(),
	};
};

AppConfig.getHOCsInUse = () => [];

/**
 * Clean up store state to prevent hydration of extra data from SSR to CSR
 * @param {object} storeState store state
 */
const reduxStoreStateCleanUp = (storeState) => {
	const entitiesToClean = [
		{
			entity: 'customer',
			ignore: ['customerGroups', 'customerPersonalizationKey', 'customerGroupsKey'],
		},
		// Add more entities as needed
	];

	entitiesToClean.forEach(({entity, ignore}) => {
		if (storeState[entity]) {
			const preservedData = {};

			(ignore || []).forEach((key) => {
				if (storeState[entity][key] !== undefined) {
					preservedData[key] = storeState[entity][key];
				}
			});

			storeState[entity] = preservedData;
		}
	});
};

export default AppConfig;
